/** What the Pre-check-in tab's pieces read beside the draft: the operator, the GET as
 *  baselines and options, the permission — and the guest-form preview's step, which the
 *  section being worked in moves (Party members → Guest 1). */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { PrecheckOption, PrecheckTenant } from '@/features/precheck/editor/sources';
import { localOptionSource, type OptionSource } from '@/features/precheck/editor/sources';
import type { WaiverDetails } from '@/features/precheck/editor/WaiverList';
import type { QuestionFacts } from '@/features/precheck/editor/QuestionsList';
import type { GuestPreviewStep } from '@/features/precheck/preview/previewModel';

import type { OperatorPreCheckBaseline } from './baselines';

export interface OperatorPrecheckData {
  topId: number;
  operatorName: string;
  tenant: PrecheckTenant;
  baseline: OperatorPreCheckBaseline;
  /** console_top_manage. */
  canEdit: boolean;
  /** The ids of the stored custom definitions (they carry a list row's label and type). */
  storedIds: ReadonlySet<string>;
  /** The operator's tag categories (questions): named by code, searched locally. */
  categories: OptionSource;
  /** What the guest form derives for a category; null when the operator has none such. */
  categoryFacts: (code: string) => QuestionFacts | null;
  /** The ENABLED waivers (the add picker). */
  enabledWaivers: OptionSource;
  /** Every waiver of the operator by id. */
  waiverDetails: (id: number) => WaiverDetails | undefined;
}

const Ctx = createContext<OperatorPrecheckData | null>(null);

export function useOperatorPrecheckData(input: {
  topId: number;
  operatorName: string;
  tenantCode: string;
  baseline: OperatorPreCheckBaseline;
  canEdit: boolean;
}): OperatorPrecheckData {
  const { topId, operatorName, tenantCode, baseline, canEdit } = input;
  const { options, definitions } = baseline.data;
  return useMemo(() => {
    // The categories have no primary key here: negative ids, named by code.
    const categoryList: PrecheckOption[] = options.categories.map((c, i) => ({ id: -(i + 1), name: c.name, code: c.code }));
    const byCode = new Map(options.categories.map((c) => [c.code, c]));
    const byId = new Map(options.waivers.map((w) => [w.id, w]));
    return {
      topId,
      operatorName,
      tenant: { id: topId, code: tenantCode },
      baseline,
      canEdit,
      storedIds: new Set(definitions.map((d) => d.id)),
      categories: localOptionSource(categoryList),
      categoryFacts: (code) => {
        const c = byCode.get(code);
        return c ? { required: c.required, multiselect: c.multiselect } : null;
      },
      enabledWaivers: localOptionSource(options.waivers.filter((w) => w.enabled).map((w) => ({ id: w.id, name: w.name }))),
      waiverDetails: (id) => {
        const w = byId.get(id);
        return w
          ? { name: w.name, enabled: w.enabled, sequence: w.sequence, languages: w.languages, tags: w.tags.map((t) => t.name) }
          : undefined;
      },
    };
  }, [topId, operatorName, tenantCode, baseline, canEdit, options, definitions]);
}

export function OperatorPrecheckProvider({ value, children }: { value: OperatorPrecheckData; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOperatorPrecheck(): OperatorPrecheckData {
  const v = useContext(Ctx);
  if (!v) throw new Error('useOperatorPrecheck must be used inside <OperatorPrecheckProvider>');
  return v;
}

// ---- the preview's step --------------------------------------------------

export interface PreviewStepValue {
  step: GuestPreviewStep;
  setStep: (step: GuestPreviewStep) => void;
}

const StepCtx = createContext<PreviewStepValue | null>(null);

export function PreviewStepProvider({ value, children }: { value: PreviewStepValue; children: ReactNode }) {
  return <StepCtx.Provider value={value}>{children}</StepCtx.Provider>;
}

export function usePreviewStep(): PreviewStepValue {
  const v = useContext(StepCtx);
  if (!v) throw new Error('usePreviewStep must be used inside <PreviewStepProvider>');
  return v;
}
